import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ConfigProvider, Input, Modal, Popover } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined, EditOutlined, EyeOutlined, DeleteOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import Permission from '../components/Util/Permission.jsx';
import { UserStatusEnum } from '../utils/enums/UserStatusEnum';

// Design tokens ported from the "Users Redesign" mock — same shell palette as
// DefaultLayout, Access (RoleSettings) and Login. Users sits inside
// DefaultLayout, but the mock's own dark surfaces are still set as CSS
// variables here (rather than inherited) so a nested ConfigProvider's
// portalled popovers can read the same hex values.
import { PRIMARY, hexToRgba, SHELL_THEME as THEME, shellAntdTheme, STATUS } from '../shellTokens';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DORMANT_DAYS = 30;

const initials = (first, last) => {
  const s = `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase();
  return s || '?';
};

const fmtLastLogin = (d) => {
  if (!d) return null;
  const date = dayjs(d);
  const now = dayjs();
  if (date.isSame(now, 'day')) return `Today ${date.format('HH:mm')}`;
  if (date.isSame(now.subtract(1, 'day'), 'day')) return `Yesterday ${date.format('HH:mm')}`;
  return date.format('D MMM HH:mm');
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/** window width < 768, matching the mock's card-list breakpoint (1c). */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

const GRID = '30px minmax(0,2.1fr) minmax(0,1.5fr) minmax(0,1fr) minmax(0,1.2fr) minmax(0,.9fr) 96px';

function Users() {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { user, permissions, darkMode } = useStateContext();
  const [modal, contextHolder] = Modal.useModal();
  const isMobile = useIsMobile();

  const canManageRoles = (permissions || []).includes('ROLE_SETTINGS');
  const canCreate = (permissions || []).includes('CREATE_USER');
  const canEdit = (permissions || []).includes('UPDATE_USER');
  const canDelete = (permissions || []).includes('DELETE_USER');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState(null); // 1 | 0 | null
  const [filterRole, setFilterRole] = useState(null); // role name | null
  const [roles, setRoles] = useState([]);

  const [selected, setSelected] = useState({});
  const [statusPopoverId, setStatusPopoverId] = useState(null); // row id | 'bulk' | null
  const [roleAssignOpen, setRoleAssignOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(null); // 'status' | 'role' | 'rows' | null

  const searchBoxRef = useRef(null);

  const fetchUsers = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const search = overrides.search ?? searchText;
    const status = 'status' in overrides ? overrides.status : filterStatus;
    const role = 'role' in overrides ? overrides.role : filterRole;
    const size = overrides.pageSize ?? pageSize;
    const field = overrides.sortField ?? sortField;
    const order = overrides.sortOrder ?? sortOrder;

    setLoading(true);
    try {
      let link = `/users/index?page=${p}&per_page=${size}&sort_by=${field}&sort_order=${order}`;
      if (search) link += `&search=${encodeURIComponent(search)}`;
      if (status !== null) link += `&status=${status}`;
      if (role) link += `&role=${encodeURIComponent(role)}`;

      const res = await axiosClient.get(link);
      setUsers(res.data.data.map((d) => ({ ...d, key: d.id })));
      setTotal(res.data.total);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, searchText, filterStatus, filterRole, pageSize, sortField, sortOrder]);

  // All/Active/Inactive counts, independent of the list's own filters — two
  // cheap reads of `.total` from the same paginated endpoint. "Dormant 30d+"
  // from the mock is dropped: it would need every user's last_login, not just
  // the current page, and nothing here supplies that cheaply.
  const fetchStats = useCallback(async () => {
    try {
      const [activeRes, inactiveRes] = await Promise.all([
        axiosClient.get('/users/index?status=1&page=1&per_page=1'),
        axiosClient.get('/users/index?status=0&page=1&per_page=1'),
      ]);
      const active = activeRes.data.total ?? 0;
      const inactive = inactiveRes.data.total ?? 0;
      setStats({ all: active + inactive, active, inactive });
    } catch {
      // Stats are supplementary — the list still works without them.
    }
  }, [axiosClient]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Role catalogue for the Role filter and bulk "Assign role". Readable by role
  // administrators and by user administrators (who need the names on the create
  // form) — the bulk assign action stays behind canManageRoles regardless.
  useEffect(() => {
    if (!canManageRoles && !canCreate) return;
    axiosClient.get('/settings/getRoles')
      .then((res) => setRoles(res.data || []))
      .catch(() => { /* role filter/assign just stay unavailable */ });
  }, [axiosClient, canManageRoles, canCreate]);

  // "/" focuses search, unless a field already has focus — same affordance the
  // mock draws on the search box.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        searchBoxRef.current?.focus();
      }
      if (e.key === 'Escape' && Object.keys(selected).length) setSelected({});
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPage(1);
    fetchUsers({ page: 1, search: value });
  };

  const applyStatus = (value) => {
    setFilterStatus(value);
    setOpenFilter(null);
    setPage(1);
    fetchUsers({ page: 1, status: value });
  };

  const applyRole = (value) => {
    setFilterRole(value);
    setOpenFilter(null);
    setPage(1);
    fetchUsers({ page: 1, role: value });
  };

  const clearAllFilters = () => {
    setFilterStatus(null);
    setFilterRole(null);
    setPage(1);
    fetchUsers({ page: 1, status: null, role: null });
  };

  const changePageSize = (size) => {
    setPageSize(size);
    setOpenFilter(null);
    setPage(1);
    fetchUsers({ page: 1, pageSize: size });
  };

  const toggleSort = (field) => {
    const nextOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(nextOrder);
    fetchUsers({ sortField: field, sortOrder: nextOrder });
  };

  const goToPage = (p) => {
    setPage(p);
    fetchUsers({ page: p });
  };

  // -- Selection (self excluded — can't bulk-act on your own account) --
  const selectableIds = users.filter((u) => u.id !== user.id).map((u) => u.id);
  const selectedCount = Object.keys(selected).length;
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected[id]);
  const toggleSelect = (id) => setSelected((s) => {
    const next = { ...s };
    if (next[id]) delete next[id]; else next[id] = true;
    return next;
  });
  const toggleSelectAll = () => setSelected((s) => {
    const next = { ...s };
    if (allSelected) selectableIds.forEach((id) => delete next[id]);
    else selectableIds.forEach((id) => { next[id] = true; });
    return next;
  });

  const refreshAfterMutation = () => {
    setSelected({});
    fetchUsers();
    fetchStats();
  };

  // -- Row actions --
  const handleUserDetails = (record) => navigate(`/users/${record.firstname}_${record.lastname}`, { state: { id: record.id } });
  const handleEditUser = (record) => navigate('/users/userForm', { state: { user: record } });

  const changeStatus = async (id, status) => {
    setStatusPopoverId(null);
    try {
      const res = await axiosClient.put(`/users/changeStatus/${id}`, { status });
      toast.success(res.data.message || 'Status updated');
      refreshAfterMutation();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const bulkChangeStatus = async (status) => {
    setStatusPopoverId(null);
    const ids = Object.keys(selected);
    modal.confirm({
      title: `Set ${ids.length} user${ids.length === 1 ? '' : 's'} to ${UserStatusEnum[status]}?`,
      okText: 'Update',
      onOk: async () => {
        const results = await Promise.allSettled(ids.map((id) => axiosClient.put(`/users/changeStatus/${id}`, { status })));
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        if (ok) toast.success(`${ok} user${ok === 1 ? '' : 's'} updated`);
        if (ok < ids.length) toast.warning(`${ids.length - ok} could not be updated`);
        refreshAfterMutation();
      },
    });
  };

  const bulkAssignRole = async (role) => {
    setRoleAssignOpen(false);
    const ids = Object.keys(selected).map(Number);
    try {
      await axiosClient.post('/roles/assignRole', { userIds: ids, role });
      toast.success(`${role} assigned to ${ids.length} user${ids.length === 1 ? '' : 's'}`);
      refreshAfterMutation();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  const handleDeleteUser = (record) => {
    const roleList = (record.roleNames || []).join(', ');
    modal.confirm({
      title: `Delete ${record.firstname} ${record.lastname}?`,
      content: roleList
        ? `Their ${roleList} role${(record.roleNames || []).length === 1 ? '' : 's'} will be removed. This cannot be undone.`
        : 'This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          const res = await axiosClient.get(`/users/delete?id=${record.id}`);
          toast.warning(res.data.message);
          refreshAfterMutation();
        } catch (error) {
          toast.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const bulkDelete = () => {
    const ids = Object.keys(selected);
    modal.confirm({
      title: `Delete ${ids.length} user${ids.length === 1 ? '' : 's'}?`,
      content: 'This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const results = await Promise.allSettled(ids.map((id) => axiosClient.get(`/users/delete?id=${id}`)));
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        if (ok) toast.warning(`${ok} user${ok === 1 ? '' : 's'} deleted`);
        if (ok < ids.length) toast.error(`${ids.length - ok} could not be deleted`);
        refreshAfterMutation();
      },
    });
  };

  // Current page only — an honest label for what's actually exported, since
  // there's no backend endpoint for a full-directory export.
  const handleExport = () => {
    const header = ['Name', 'Email', 'Phone', 'Roles', 'Status', 'Last Login'];
    const rows = users.map((u) => [
      `${u.firstname} ${u.lastname}`.trim(),
      u.email,
      u.address?.phone || '',
      (u.roleNames || []).join('; '),
      UserStatusEnum[u.status ? 1 : 0],
      u.last_login ? dayjs(u.last_login).format('YYYY-MM-DD HH:mm') : 'Never',
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? 'dark' : 'light']).map(([k, v]) => [`--${k}`, v])
    ),
    '--primary': PRIMARY,
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const hasActiveFilters = filterStatus !== null || !!filterRole;
  const showEmpty = !loading && users.length === 0;

  return (
    <div className="et-users" style={rootStyle}>
      <style>{`
        .et-users{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-users__row:hover{background:var(--hover)}
        .et-users__card:hover{border-color:var(--borderStrong)}
        .et-users__iconbtn:hover{background:var(--surface2);color:var(--text)}
        .et-users__iconbtn--danger:hover{background:rgba(248,113,113,.12);color:${STATUS.error}}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Users</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            {total} user{total === 1 ? '' : 's'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            title="Export this page as CSV"
            onClick={handleExport}
            style={ghostBtn}
          >
            <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> Export
          </button>
          <Permission required="ROLE_SETTINGS">
            <button type="button" onClick={() => navigate('/settings/roleSettings')} style={ghostBtn}>
              Manage access
            </button>
          </Permission>
          <Permission required="CREATE_USER">
            <button type="button" onClick={() => navigate('/users/userForm')} style={primaryBtn}>
              + Add User
            </button>
          </Permission>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 14 }}>
          <StatTile label="All users" value={stats.all} tint={PRIMARY} icon="◍" active={filterStatus === null} onClick={() => applyStatus(null)} />
          <StatTile label="Active" value={stats.active} tint={STATUS.ok} icon="●" active={filterStatus === 1} onClick={() => applyStatus(1)} />
          <StatTile label="Inactive" value={stats.inactive} tint={STATUS.errorSoft} icon="○" active={filterStatus === 0} onClick={() => applyStatus(0)} />
        </div>
      )}

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

          {/* Toolbar */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ font: '700 14.5px Outfit, sans-serif' }}>All users</div>
              <div style={{ font: '600 11.5px var(--mono, monospace)', color: 'var(--dim)', padding: '2px 7px', borderRadius: 9, background: 'var(--surface2)' }}>{total}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Input
                ref={searchBoxRef}
                allowClear
                placeholder="Search name, email or phone"
                prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
                suffix={<span style={{ font: '600 10.5px monospace', color: 'var(--dim)', padding: '2px 6px', borderRadius: 5, background: 'var(--surface)', border: '1px solid var(--border)' }}>/</span>}
                style={{ width: 260 }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onPressEnter={() => handleSearch(searchInput)}
                onClear={() => handleSearch('')}
              />
              <Popover
                trigger="click"
                open={openFilter === 'status'}
                onOpenChange={(o) => setOpenFilter(o ? 'status' : null)}
                content={
                  <MenuList
                    items={[
                      { key: null, label: 'All statuses' },
                      { key: 1, label: 'Active', dot: STATUS.ok },
                      { key: 0, label: 'Inactive', dot: STATUS.errorSoft },
                    ]}
                    active={filterStatus}
                    onSelect={applyStatus}
                  />
                }
              >
                <button type="button" style={filterBtn}>Status <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} /></button>
              </Popover>
              {canManageRoles && (
                <Popover
                  trigger="click"
                  open={openFilter === 'role'}
                  onOpenChange={(o) => setOpenFilter(o ? 'role' : null)}
                  content={
                    <MenuList
                      items={[{ key: null, label: 'All roles' }, ...roles.map((r) => ({ key: r.name, label: r.name }))]}
                      active={filterRole}
                      onSelect={applyRole}
                    />
                  }
                >
                  <button type="button" style={filterBtn}>Role <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} /></button>
                </Popover>
              )}
            </div>
          </div>

          {/* Filter chips */}
          {hasActiveFilters && (
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase', marginRight: 2 }}>Filters</span>
              {filterStatus !== null && <Chip label="Status" value={UserStatusEnum[filterStatus]} onClear={() => applyStatus(null)} tone="err" />}
              {filterRole && <Chip label="Role" value={filterRole} onClear={() => applyRole(null)} />}
              <button type="button" onClick={clearAllFilters} style={{ marginLeft: 'auto', height: 26, padding: '0 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', font: '600 12px Outfit, sans-serif', cursor: 'pointer' }}>
                Clear all
              </button>
            </div>
          )}

          {/* Bulk action bar */}
          {selectedCount > 0 && (
            <div style={{ padding: '9px 18px', borderBottom: '1px solid var(--border)', background: 'var(--activeBg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ font: '600 13px Outfit, sans-serif' }}>{selectedCount} selected</span>
                <span onClick={() => setSelected({})} style={{ font: '600 12.5px Outfit, sans-serif', color: 'var(--activeText)', cursor: 'pointer' }}>Clear</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {canEdit && (
                  <Popover
                    trigger="click"
                    open={statusPopoverId === 'bulk'}
                    onOpenChange={(o) => setStatusPopoverId(o ? 'bulk' : null)}
                    content={
                      <MenuList
                        items={[{ key: 1, label: 'Active', dot: STATUS.ok }, { key: 0, label: 'Inactive', dot: STATUS.errorSoft }]}
                        onSelect={bulkChangeStatus}
                        hint="Inactive users keep their roles but cannot sign in."
                      />
                    }
                  >
                    <button type="button" style={barBtn}>Set status <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} /></button>
                  </Popover>
                )}
                {canManageRoles && (
                  <Popover
                    trigger="click"
                    open={roleAssignOpen}
                    onOpenChange={setRoleAssignOpen}
                    content={<MenuList items={roles.map((r) => ({ key: r.name, label: r.name }))} onSelect={bulkAssignRole} />}
                  >
                    <button type="button" style={barBtn}>Assign role <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} /></button>
                  </Popover>
                )}
                {canDelete && (
                  <button type="button" onClick={bulkDelete} style={dangerBtn}>Delete</button>
                )}
              </div>
            </div>
          )}

          {/* Desktop table */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div style={{ minWidth: 760 }}>
                <div style={{ display: 'grid', gridTemplateColumns: GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={checkboxStyle} />
                  </div>
                  {[
                    { key: 'firstname', label: 'Name', sortable: true },
                    { key: 'roles', label: 'Roles' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'last_login', label: 'Last login', sortable: true },
                    { key: 'status', label: 'Status', sortable: true },
                    { key: 'action', label: 'Action' },
                  ].map((h) => (
                    <div
                      key={h.key}
                      onClick={h.sortable ? () => toggleSort(h.key) : undefined}
                      style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 5, font: '700 11.5px Outfit, sans-serif', color: sortField === h.key ? 'var(--activeText)' : 'var(--muted)', letterSpacing: '.05em', textTransform: 'uppercase', cursor: h.sortable ? 'pointer' : 'default' }}
                    >
                      {h.label}
                      {h.sortable && sortField === h.key && <span style={{ color: PRIMARY, fontSize: 9 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  ))}
                </div>

                {showEmpty && (
                  <EmptyState search={searchText} onClear={() => { setSearchInput(''); handleSearch(''); clearAllFilters(); }} onAdd={() => navigate('/users/userForm')} canAdd={canCreate} hasFilters={hasActiveFilters || !!searchText} />
                )}

                {!loading && users.map((record) => {
                  const isSelf = record.id === user.id;
                  const isDormant = !!record.status && record.last_login && dayjs().diff(dayjs(record.last_login), 'day') > DORMANT_DAYS;
                  const isSelected = !!selected[record.id];
                  return (
                    <div
                      key={record.id}
                      className="et-users__row"
                      style={{
                        display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)',
                        background: isSelected ? 'var(--activeBg)' : isSelf ? 'var(--hover)' : 'transparent',
                        boxShadow: isSelected ? 'inset 3px 0 0 var(--primary)' : 'none',
                        opacity: record.status ? 1 : 0.72,
                      }}
                    >
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <input type="checkbox" checked={isSelected} disabled={isSelf} onChange={() => toggleSelect(record.id)} style={checkboxStyle} title={isSelf ? "You can't bulk-act on your own account" : undefined} />
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                        <UserAvatar record={record} highlight={isSelf} />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                          <span style={{ font: '600 13px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {record.firstname} {record.lastname}
                            {isSelf && <span style={{ font: '600 10.5px Outfit, sans-serif', color: 'var(--activeText)', background: 'var(--activeBg)', padding: '1px 6px', borderRadius: 8, marginLeft: 4 }}>You</span>}
                          </span>
                          <span style={{ font: '400 11.5px monospace', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.email}</span>
                        </span>
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                        <RoleChips roles={record.roleNames} />
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', font: '400 12.5px monospace', color: 'var(--muted)' }}>
                        {record.address?.phone || <span style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--dim)' }}>Not set</span>}
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', font: '400 12.5px monospace', color: isDormant ? STATUS.warn : 'var(--text)' }}>
                        {fmtLastLogin(record.last_login) || <span style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--dim)' }}>Never</span>}
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center' }}>
                        <StatusPill
                          record={record}
                          canEdit={canEdit && !isSelf}
                          open={statusPopoverId === record.id}
                          onOpenChange={(o) => setStatusPopoverId(o ? record.id : null)}
                          onChange={(status) => changeStatus(record.id, status)}
                        />
                      </div>
                      <div style={{ padding: '9px 6px', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconBtn title="View" onClick={() => handleUserDetails(record)}><EyeOutlined /></IconBtn>
                        <Permission required="UPDATE_USER">
                          <IconBtn title="Edit" onClick={() => handleEditUser(record)}><EditOutlined /></IconBtn>
                        </Permission>
                        <Permission required="DELETE_USER">
                          <IconBtn title="Delete" danger disabled={isSelf} onClick={() => handleDeleteUser(record)}><DeleteOutlined /></IconBtn>
                        </Permission>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mobile card list */}
          {isMobile && (
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
              {showEmpty && (
                <EmptyState search={searchText} onClear={() => { setSearchInput(''); handleSearch(''); clearAllFilters(); }} onAdd={() => navigate('/users/userForm')} canAdd={canCreate} hasFilters={hasActiveFilters || !!searchText} />
              )}
              {!loading && users.map((record) => {
                const isSelf = record.id === user.id;
                return (
                  <div key={record.id} className="et-users__card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 10, opacity: record.status ? 1 : 0.72 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                      <UserAvatar record={record} highlight={isSelf} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ font: '600 14px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.firstname} {record.lastname}{isSelf ? ' · You' : ''}</div>
                        <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.email}</div>
                      </div>
                      <span style={{ font: '700 11px Outfit, sans-serif', color: record.status ? STATUS.ok : STATUS.errorSoft, background: record.status ? 'rgba(52,211,153,.16)' : 'rgba(248,113,113,.14)', padding: '4px 8px', borderRadius: 11 }}>
                        {UserStatusEnum[record.status ? 1 : 0]}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <RoleChips roles={record.roleNames} />
                      <span style={{ marginLeft: 'auto', font: '400 11.5px monospace', color: 'var(--dim)' }}>{fmtLastLogin(record.last_login) || 'Never'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                      <button type="button" onClick={() => handleUserDetails(record)} style={mobileActionBtn}>View</button>
                      <Permission required="UPDATE_USER">
                        <button type="button" onClick={() => handleEditUser(record)} style={mobileActionBtn}>Edit</button>
                      </Permission>
                      <Permission required="DELETE_USER">
                        <button type="button" disabled={isSelf} onClick={() => handleDeleteUser(record)} style={{ ...mobileActionBtn, flex: 'none', width: 44, color: STATUS.error, opacity: isSelf ? 0.4 : 1 }}>
                          <DeleteOutlined />
                        </button>
                      </Permission>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                {total === 0 ? '0 results' : <>Showing <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{rangeStart}–{rangeEnd}</span> of <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{total}</span></>}
              </div>
              <Popover
                trigger="click"
                open={openFilter === 'rows'}
                onOpenChange={(o) => setOpenFilter(o ? 'rows' : null)}
                content={<MenuList items={PAGE_SIZE_OPTIONS.map((n) => ({ key: n, label: String(n) }))} active={pageSize} onSelect={changePageSize} />}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                  <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Rows</span>
                  <span style={{ height: 28, padding: '0 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12px monospace', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {pageSize}<DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                  </span>
                </span>
              </Popover>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <PageBtn disabled={page <= 1} onClick={() => goToPage(page - 1)}>‹</PageBtn>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .map((p, i, arr) => (
                  <React.Fragment key={p}>
                    {i > 0 && arr[i - 1] !== p - 1 && <span style={{ color: 'var(--dim)', fontSize: 12 }}>…</span>}
                    <PageBtn active={p === page} onClick={() => goToPage(p)}>{p}</PageBtn>
                  </React.Fragment>
                ))}
              <PageBtn disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>›</PageBtn>
            </div>
          </div>
        </div>
        {contextHolder}
      </ConfigProvider>
    </div>
  );
}

/* ---------------------------------------------------------------------- */

const ghostBtn = {
  height: 38, padding: '0 14px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', font: '600 13px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
};
const primaryBtn = {
  height: 38, padding: '0 18px', borderRadius: 9, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 13px Outfit, sans-serif', cursor: 'pointer',
};
const filterBtn = {
  height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
};
const barBtn = {
  height: 30, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
};
const dangerBtn = {
  height: 30, padding: '0 12px', borderRadius: 8, border: `1px solid ${STATUS.error}59`, background: 'rgba(248,113,113,.1)',
  color: STATUS.error, font: '600 12.5px Outfit, sans-serif', cursor: 'pointer',
};
const mobileActionBtn = {
  flex: 1, height: 44, borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const checkboxStyle = { width: 15, height: 15, accentColor: PRIMARY, cursor: 'pointer' };

function StatTile({ label, value, tint, icon, active, onClick }) {
  return (
    <div
      role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      style={{
        background: 'var(--surface)', border: `1px solid ${active ? tint : 'var(--border)'}`, borderRadius: 14,
        padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5, cursor: 'pointer',
        boxShadow: active ? `0 0 0 1px ${tint}` : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 19, height: 19, borderRadius: 6, background: `${tint}29`, color: tint, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
        <span style={{ font: '600 11.5px Outfit, sans-serif', color: 'var(--muted)', letterSpacing: '.02em', textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ font: '700 18px var(--mono, monospace)', color: 'var(--text)' }}>{value}</div>
    </div>
  );
}

function MenuList({ items, active, onSelect, hint }) {
  return (
    <div style={{ minWidth: 168, background: 'var(--surface2)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: 6 }}>
        {items.map((it) => {
          const isActive = active !== undefined && active === it.key;
          return (
            <div
              key={String(it.key)}
              onClick={() => onSelect(it.key)}
              style={{ height: 34, borderRadius: 8, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', background: isActive ? 'var(--hover)' : 'transparent' }}
            >
              {it.dot && <span style={{ color: it.dot, fontSize: 11 }}>●</span>}
              <span style={{ font: '600 12.5px Outfit, sans-serif', color: 'var(--text)' }}>{it.label}</span>
              {isActive && <CheckOutlined style={{ marginLeft: 'auto', color: PRIMARY, fontSize: 11 }} />}
            </div>
          );
        })}
      </div>
      {hint && (
        <div style={{ padding: '9px 12px', borderTop: '1px solid var(--border)', font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, onClear, tone }) {
  const isErr = tone === 'err';
  return (
    <span style={{
      height: 26, padding: '0 5px 0 9px', borderRadius: 7,
      background: isErr ? 'rgba(248,113,113,.12)' : 'var(--surface2)',
      border: isErr ? '1px solid rgba(248,113,113,.35)' : '1px solid var(--border)',
      color: isErr ? STATUS.errorSoft : 'var(--text)',
      font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <span style={{ opacity: .75, fontWeight: 500, color: isErr ? STATUS.errorSoft : 'var(--muted)' }}>{label}</span>
      {value}
      <span onClick={onClear} style={{ width: 16, height: 16, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, cursor: 'pointer', background: isErr ? 'rgba(248,113,113,.16)' : 'var(--surface)' }}>
        <CloseOutlined />
      </span>
    </span>
  );
}

function UserAvatar({ record, highlight }) {
  if (record.image) {
    const src = `${process.env.REACT_APP_API_BASE_URL}/files/upload/${record.image.id}`;
    return <img src={src} alt="" style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 16, objectFit: 'cover' }} />;
  }
  return (
    <span style={{
      flexShrink: 0, width: 32, height: 32, borderRadius: 16,
      background: highlight ? hexToRgba(PRIMARY, .18) : 'var(--surface2)',
      color: highlight ? 'var(--activeText)' : 'var(--muted)',
      font: '600 12px monospace', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {initials(record.firstname, record.lastname)}
    </span>
  );
}

// Shows the first two roles, then a real "+N" overflow (never a fabricated count).
function RoleChips({ roles }) {
  const list = roles || [];
  if (list.length === 0) {
    return <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)', background: 'transparent', border: '1px dashed var(--borderStrong)', padding: '3px 8px', borderRadius: 5 }}>No role</span>;
  }
  const shown = list.slice(0, 2);
  const rest = list.length - shown.length;
  return (
    <>
      {shown.map((name) => {
        const isSudo = name === 'SUDO';
        return (
          <span key={name} style={{
            font: '600 11px Outfit, sans-serif',
            color: isSudo ? STATUS.warn : 'var(--text)',
            background: isSudo ? 'rgba(251,191,36,.14)' : 'var(--surface2)',
            padding: '3px 8px', borderRadius: 5,
          }}>
            {name}
          </span>
        );
      })}
      {rest > 0 && (
        <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--activeText)', background: 'var(--activeBg)', padding: '3px 8px', borderRadius: 5 }}>+{rest}</span>
      )}
    </>
  );
}

function StatusPill({ record, canEdit, open, onOpenChange, onChange }) {
  const active = !!record.status;
  const pill = (
    <span
      onClick={canEdit ? () => onOpenChange(true) : undefined}
      style={{
        font: '700 11.5px Outfit, sans-serif', color: active ? STATUS.ok : STATUS.errorSoft,
        background: active ? 'rgba(52,211,153,.16)' : 'rgba(248,113,113,.14)',
        padding: '4px 8px', borderRadius: 11, display: 'flex', alignItems: 'center', gap: 6,
        cursor: canEdit ? 'pointer' : 'default',
      }}
    >
      {active ? '●' : '○'}<span>{UserStatusEnum[active ? 1 : 0]}</span>
      {canEdit && <span style={{ fontSize: 8, opacity: .7 }}>▾</span>}
    </span>
  );
  if (!canEdit) return pill;
  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={onOpenChange}
      content={
        <MenuList
          items={[{ key: 1, label: 'Active', dot: STATUS.ok }, { key: 0, label: 'Inactive', dot: STATUS.errorSoft }]}
          active={active ? 1 : 0}
          onSelect={onChange}
          hint="Inactive users keep their roles but cannot sign in."
        />
      }
    >
      {pill}
    </Popover>
  );
}

function IconBtn({ children, onClick, title, danger, disabled }) {
  return (
    <span
      className={danger ? 'et-users__iconbtn et-users__iconbtn--danger' : 'et-users__iconbtn'}
      title={title}
      onClick={disabled ? undefined : onClick}
      style={{
        width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 7,
        cursor: disabled ? 'default' : 'pointer', color: disabled ? 'var(--dim)' : 'var(--dim)', opacity: disabled ? 0.35 : 1, fontSize: 12,
      }}
    >
      {children}
    </span>
  );
}

function PageBtn({ children, onClick, active, disabled }) {
  return (
    <span
      onClick={disabled ? undefined : onClick}
      style={{
        height: 30, minWidth: 30, padding: '0 9px', borderRadius: 8,
        border: active ? 'none' : '1px solid var(--border)',
        background: active ? PRIMARY : 'var(--surface2)',
        color: active ? '#fff' : disabled ? 'var(--dim)' : 'var(--text)',
        font: active ? '700 12px monospace' : '600 12px monospace',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </span>
  );
}

function EmptyState({ search, hasFilters, onClear, onAdd, canAdd }) {
  return (
    <div style={{ padding: '34px 18px 38px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SearchOutlined />
      </span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>
        {search ? <>No users match <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--activeText)' }}>{search}</span></> : 'No users match these filters'}
      </div>
      <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)', textAlign: 'center', maxWidth: 280 }}>
        Search covers name, email and phone. Filters are still applied.
      </div>
      <div style={{ display: 'flex', gap: 9, marginTop: 4 }}>
        {hasFilters && <button type="button" onClick={onClear} style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer' }}>Clear filters</button>}
        {canAdd && <button type="button" onClick={onAdd} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: PRIMARY, color: '#fff', font: '700 12.5px Outfit, sans-serif', cursor: 'pointer' }}>+ Add User</button>}
      </div>
    </div>
  );
}

export default Users;
